// Email validation
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Phone validation (South African format)
export const isValidPhone = (phone) => {
  const phoneRegex = /^[0-9]{10}$/;
  const cleanPhone = phone.replace(/[\s\-()]/g, "");
  return phoneRegex.test(cleanPhone);
};

// South African ID number validation
export const isValidSAIdNumber = (idNumber) => {
  // Basic validation: 13 digits
  const idRegex = /^[0-9]{13}$/;
  if (!idRegex.test(idNumber)) {
    return false;
  }

  // Additional validation: check if date part is valid
  const month = idNumber.substring(2, 4);
  const day = idNumber.substring(4, 6);

  const monthNum = parseInt(month);
  const dayNum = parseInt(day);

  if (monthNum < 1 || monthNum > 12) {
    return false;
  }

  if (dayNum < 1 || dayNum > 31) {
    return false;
  }

  return true;
};

// Bank account number validation
export const isValidAccountNumber = (accountNumber) => {
  const cleanAccount = accountNumber.replace(/[\s-]/g, "");
  return (
    cleanAccount.length >= 8 &&
    cleanAccount.length <= 11 &&
    /^[0-9]+$/.test(cleanAccount)
  );
};

// Branch code validation
export const isValidBranchCode = (branchCode) => {
  const cleanCode = branchCode.replace(/[\s-]/g, "");
  return cleanCode.length === 6 && /^[0-9]+$/.test(cleanCode);
};

// File size validation (in bytes)
export const isValidFileSize = (file, maxSizeInMB = 5) => {
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};

// Format file size for display
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

// Validate contact form
export const validateContactForm = (formData) => {
  const errors = {};

  if (!formData.name || formData.name.trim().length === 0) {
    errors.name = "Name is required";
  }

  if (!formData.email || !isValidEmail(formData.email)) {
    errors.email = "Valid email is required";
  }

  if (!formData.phone || !isValidPhone(formData.phone)) {
    errors.phone = "Valid 10-digit phone number is required";
  }

  if (!formData.message || formData.message.trim().length === 0) {
    errors.message = "Message is required";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Validate loan application
export const validateLoanApplication = (formData) => {
  const errors = {};

  // Personal Info
  if (
    !formData.personalInfo.fullName ||
    formData.personalInfo.fullName.trim().length === 0
  ) {
    errors.fullName = "Full name is required";
  }

  if (
    !formData.personalInfo.email ||
    !isValidEmail(formData.personalInfo.email)
  ) {
    errors.email = "Valid email is required";
  }

  if (
    !formData.personalInfo.phone ||
    !isValidPhone(formData.personalInfo.phone)
  ) {
    errors.phone = "Valid 10-digit phone number is required";
  }

  if (
    !formData.personalInfo.idNumber ||
    !isValidSAIdNumber(formData.personalInfo.idNumber)
  ) {
    errors.idNumber = "Valid South African ID number is required (13 digits)";
  }

  // Loan Details
  if (!formData.loanDetails.amount || formData.loanDetails.amount <= 0) {
    errors.amount = "Valid loan amount is required";
  }

  if (!formData.loanDetails.period || formData.loanDetails.period <= 0) {
    errors.period = "Valid repayment period is required";
  }

  // Employment Info - Conditional validation based on employment status
  const employmentStatus = formData.employmentInfo.employmentStatus;

  if (!employmentStatus) {
    errors.employmentStatus = "Employment status is required";
  }

  // Monthly income is required for everyone
  if (
    !formData.employmentInfo.monthlyIncome ||
    formData.employmentInfo.monthlyIncome <= 0
  ) {
    errors.monthlyIncome = "Valid monthly income is required";
  }

  // Conditional validations based on employment status
  if (employmentStatus === "employed") {
    if (
      !formData.employmentInfo.employer ||
      formData.employmentInfo.employer.trim().length === 0
    ) {
      errors.employer = "Employer name is required for employed applicants";
    }

    if (
      !formData.employmentInfo.position ||
      formData.employmentInfo.position.trim().length === 0
    ) {
      errors.position = "Position is required for employed applicants";
    }

    if (
      !formData.employmentInfo.contact ||
      formData.employmentInfo.contact.trim().length === 0
    ) {
      errors.contact = "Employer contact is required for employed applicants";
    }

    if (
      !formData.employmentInfo.duration ||
      formData.employmentInfo.duration.trim().length === 0
    ) {
      errors.duration =
        "Employment duration is required for employed applicants";
    }
  } else if (employmentStatus === "unemployed") {
    if (
      !formData.employmentInfo.incomeSource ||
      formData.employmentInfo.incomeSource.trim().length === 0
    ) {
      errors.incomeSource =
        "Income source is required for unemployed applicants";
    }
  } else if (employmentStatus === "sassa") {
    if (
      !formData.employmentInfo.grantType ||
      formData.employmentInfo.grantType.trim().length === 0
    ) {
      errors.grantType = "Grant type is required for SASSA grant recipients";
    }
  }

  // Banking Details
  if (
    !formData.bankingDetails.bankName ||
    formData.bankingDetails.bankName.trim().length === 0
  ) {
    errors.bankName = "Bank name is required";
  }

  if (
    !formData.bankingDetails.accountType ||
    formData.bankingDetails.accountType.trim().length === 0
  ) {
    errors.accountType = "Account type is required";
  }

  if (
    !formData.bankingDetails.accountNumber ||
    !isValidAccountNumber(formData.bankingDetails.accountNumber)
  ) {
    errors.accountNumber = "Valid account number is required (8-11 digits)";
  }

  if (
    !formData.bankingDetails.branchCode ||
    !isValidBranchCode(formData.bankingDetails.branchCode)
  ) {
    errors.branchCode = "Valid branch code is required (6 digits)";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};
